/** @file Emits the direction set table (modes, sources, flips, fix layers) with animations as indices. */
import { isoTileHeight } from '@pose2d/core/direction/isoTileHeight';
import { drawOrderOpSlots } from '@pose2d/format/doc/drawOrderOpSlots';
import { names } from '@pose2d/format/names';
import type { Dir, DirectionEntry, Doc, DocIndex, DrawOrderOp, Pose2DJson } from '@pose2d/format/types';
import { requireCompiledIndex } from './requireCompiledIndex';

type SetJson = NonNullable<Pose2DJson['directions']>;
type EntryJson = NonNullable<SetJson['entries'][Dir]>;
type FixJson = NonNullable<EntryJson['fix']>;
type Maps = { skinIndex: Map<string, number>; animationIndex: Map<string, number>; slotIndex: Map<string, number> };

/** The set's own `iso`, else `view.grid` when it is iso (isoAngle 30: tileH = core isoTileHeight(tileW), W2-24). */
function isoOf(doc: Doc): [number, number] | null {
  const own = doc.directions?.iso;
  if (own !== undefined) return [own[0], own[1]];
  const g = doc.view.grid;
  if (g.mode !== 'iso') return null;
  return [g.tileW, g.isoAngle === 30 ? isoTileHeight(g.tileW) : g.tileH];
}

function compileOp(op: DrawOrderOp, slotIndex: ReadonlyMap<string, number>, path: string): [DrawOrderOp['op'], number, number] {
  const [a, b] = drawOrderOpSlots(op);
  return [op.op, requireCompiledIndex(slotIndex, a, 'slot', path), requireCompiledIndex(slotIndex, b, 'slot', path)];
}

/** Fix skin, ops and animations (keys by base name in code-unit order, R12-01) with indices. */
function compileFix(fix: NonNullable<DirectionEntry['fix']>, maps: Maps, at: string): FixJson {
  const { skin, drawOrderOps, animations } = fix;
  return {
    ...(skin === undefined ? {} : { skin: requireCompiledIndex(maps.skinIndex, skin, 'skin', `${at}.skin`) }),
    ...(drawOrderOps === undefined ? {} : { drawOrderOps: drawOrderOps.map((op, i) => compileOp(op, maps.slotIndex, `${at}.drawOrderOps.${i}`)) }),
    ...(animations === undefined ? {} : {
      animations: Object.fromEntries(Object.entries(animations).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([base, id]) => [base, requireCompiledIndex(maps.animationIndex, id, 'animation', `${at}.animations.${base}`)])),
    }),
  };
}

function compileEntry(entry: DirectionEntry, maps: Maps, at: string): EntryJson {
  return {
    mode: entry.mode,
    ...(entry.source === undefined ? {} : { source: entry.source }),
    ...(entry.flipX === undefined ? {} : { flipX: entry.flipX }),
    ...(entry.fix === undefined ? {} : { fix: compileFix(entry.fix, maps, `${at}.fix`) }),
  };
}

/** Returns undefined when the document has no direction set. */
export function compileDirections(doc: Doc, index: DocIndex, maps: { skinIndex: Map<string, number>; animationIndex: Map<string, number>; slotIndex: Map<string, number> }): Pose2DJson['directions'] {
  const set = doc.directions;
  if (set === undefined) return undefined;
  const entries: SetJson['entries'] = {};
  for (const dir of names.DIRS) {
    const entry = set.entries[dir];
    if (entry !== undefined) entries[dir] = compileEntry(entry, maps, `directions.entries.${dir}`);
  }
  const iso = isoOf(doc);
  return { count: set.count, ...(set.layout4 === undefined ? {} : { layout4: set.layout4 }), ...(iso === null ? {} : { iso }), entries };
}
